import { useCallback, useDebugValue, useMemo } from "react";
import { useSkillExceptions } from "./useSkillExceptions";
import { differenceInMilliseconds, endOfDay, subDays, startOfDay } from 'date-fns'
import { useStateWithIntervalUpdate } from "./useStateWithIntervalUpdate";
import { useSkillOpeningHours } from "./useSkillOpeningHours";
import uniteDistancesWithSimilarity from "../../../tools/uniteDistancesWithSimilarity";

export function useSlaTimeInExceptionOrOutsideOpeningHours(conversation, updateEvery = 1000) {
  const { pullAt, skillId } = conversation

  const createdAt = useMemo(() => new Date(conversation.createdAt), [conversation.createdAt])

  const relevanceDate = useMemo(() => pullAt ?? createdAt, [pullAt, createdAt])

  const exceptions = useSkillExceptions(skillId)

  const skillOpeningHours = useSkillOpeningHours(skillId)

  const openingHours = useMemo(() => {
    const day = new Date().getDay();

    if (!skillOpeningHours) return 0

    if (skillOpeningHours[day]) return skillOpeningHours[day]

    return 1
  }, [skillOpeningHours])

  const exceptionsWithOutsideOpeningHours = useMemo(() => {
    const now = new Date()

    if (openingHours === 1) return uniteDistancesWithSimilarity([
      ...exceptions,
      [startOfDay(now), endOfDay(now)]
    ])

    if (openingHours === 0) return uniteDistancesWithSimilarity(exceptions)

    const [openingHoursStart, openingHoursEnd] = openingHours
    const startAt = new Date(`${now.toDateString()} ${openingHoursStart}`);
    const endAt = new Date(`${now.toDateString()} ${openingHoursEnd}`);

    const outsideOpeningHoursExceptions = [
      [startOfDay(startAt), startAt],
      [endAt, endOfDay(endAt)]
    ]

    return uniteDistancesWithSimilarity([
      ...exceptions,
      ...outsideOpeningHoursExceptions
    ])
  }, [exceptions, openingHours])

  const timeInExceptionOrOutsideOpeningHours = useStateWithIntervalUpdate(
    useCallback(() => {
      let current = new Date();
      let ms = 0;

      do {
        for (const exception of exceptionsWithOutsideOpeningHours) {
          const [startAt, endAt] = exception

          if (relevanceDate > endAt) continue

          if (current < startAt) continue

          if (current > endAt) ms += differenceInMilliseconds(endAt, startAt)

          else ms += differenceInMilliseconds(current, startAt)

          ms -= Math.max(differenceInMilliseconds(relevanceDate, startAt), 0) 
        }

        current = endOfDay(subDays(current, 1));
      } while (current > relevanceDate);

      return ms;
    }, [exceptionsWithOutsideOpeningHours, relevanceDate]),
    updateEvery
  )

  useDebugValue(`conversation is in the exception period or outside opening hours for ${timeInExceptionOrOutsideOpeningHours} ms`)

  return timeInExceptionOrOutsideOpeningHours
}
